import React, { useState, useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import "./CreateGroupModal.css";
import api from "../../api/apiClient";
import { createGraphNode } from "../../redux/slices/graphSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useUserId } from "../../Utils/helpers";
import { Rnd } from "react-rnd";
import ConfirmationModal from "./ConfirmationModal";
import { useSchemaGraph } from "../../hooks/useSchemaGraph";
import FOUNDATION_CLASS from "../../Utils/constants";

const CreateNode = ({ show, handleClose, initialNodeData }) => {
  const [formData, setFormData] = useState({});
  const [file, setFile] = useState(null);
  const [filepath, setFilepath] = useState("");
  const [relationInfo, setRelationInfo] = useState([]);
  const dispatch = useDispatch();
  const userId = useUserId();
  const fileInputRef = useRef(null);
  const [showOthersField, setShowOthersField] = useState(false);
  const [currentNodeData, setCurrentNodeData] = useState(initialNodeData);

  const { cy } = useSchemaGraph();

  const [size, setSize] = useState({
    width: window.innerWidth * 0.38,
    height: window.innerHeight - 76,
  });

  const [position, setPosition] = useState({
    x: window.innerWidth * 0.225,
    y: 80,
  });

  const [showModal, setShowModal] = useState(false);

  const orderedFieldKeysPlaybookRule = [
    "actiontext",
    "questiontext",
    "name",
    "desc",
  ];

  // Update local node data when initial data changes
  useEffect(() => {
    setCurrentNodeData(initialNodeData);
  }, [initialNodeData]);

  useEffect(() => {
    if (show && currentNodeData && cy) {
      const classInfo = showClassNodeInfo(currentNodeData, cy);
      setRelationInfo(classInfo.relinfo);
    }
  }, [show, currentNodeData, cy]);

  const handleClassClick = (className) => {
    if (!cy) return;

    // Find the node in cy using the class name
    const foundNode = cy.nodes(`node[name='${className}']`).first();

    if (foundNode.length > 0) {
      // Get the node data and props
      setCurrentNodeData(foundNode.data().src_obj);

      setFormData({});
      setFile(null);
      setFilepath("");
      setShowOthersField(false);
    }
  };

  // Modified to return React elements instead of HTML strings
  const ClassLink = ({ className }) => (
    <span
      className="text-danger cursor-pointer"
      onClick={(e) => {
        e.preventDefault(); // Prevent any default behavior
        e.stopPropagation(); // Prevent event bubbling
        handleClassClick(className);
      }}
      style={{
        cursor: "pointer",
      }}
    >
      {className}
    </span>
  );

  function extractFilename(value) {
    if (value) {
      const lastSlashIndex = value.lastIndexOf("/");
      const lastBackslashIndex = value.lastIndexOf("\\");

      // If the value contains slashes or backslashes, extract the filename
      if (lastSlashIndex !== -1 || lastBackslashIndex !== -1) {
        const separatorIndex = Math.max(lastSlashIndex, lastBackslashIndex);
        value = value.slice(separatorIndex + 1); // Extract the filename
      }

      return value;
    }
  }

  const Description = ({ currentNodeData, handleClassClick }) => {
    const description =
      currentNodeData.props.desc || "No description available.";

    // Remove :: or : that aren't followed by "Obligation"
    const cleanDescription = description.replace(/(::|:)(?!Obligation\b)/g, "");

    // Create a regex pattern that matches any foundation class
    const foundationClassPattern = new RegExp(
      `(${FOUNDATION_CLASS.join("|")})`,
      "g"
    );

    // Split the text into parts, with foundation classes separated
    const parts = cleanDescription.split(foundationClassPattern);

    return (
      <div>
        {parts.map((part, index) => {
          // Check if this part is a foundation class
          if (FOUNDATION_CLASS.includes(part)) {
            return (
              <ClassLink
                key={index}
                className={part}
                handleClassClick={handleClassClick}
              />
            );
          }
          // Return regular text
          return <span key={index}>{part}</span>;
        })}
      </div>
    );
  };

  // Modified to use React components
  const RelationItem = ({ relation }) => {
    const { relname, direction, nodeName, targetClass } = relation;
    return (
      <li>
        {relname}: {nodeName} {direction === "to" ? "← " : "→ "}
        <ClassLink className={targetClass} />
      </li>
    );
  };

  const showClassNodeInfo = (classNode, cy) => {
    const class_props = classNode.props;
    const name = class_props?.name;
    const desc = class_props.desc;
    const node_id = classNode.id;
    const cls_rels = getRelationsForClass(node_id, cy);
    const node_name = classNode.name;

    let relinfo = [];

    // Modified to create structured relation objects
    cls_rels.from.forEach((relcls) => {
      const reldst = relcls.outgoers(".RANGE").targets();
      const dstname = reldst.data("name");
      const relname = relcls.data("name");
      relinfo.push({
        relname,
        direction: "from",
        nodeName: node_name,
        targetClass: dstname,
      });
    });

    cls_rels.to.forEach((relcls) => {
      const relsrc = relcls.outgoers(".DOMAIN").targets();
      const srcname = relsrc.data("name");
      const relname = relcls.data("name");
      relinfo.push({
        relname,
        direction: "to",
        nodeName: node_name,
        targetClass: srcname,
      });
    });

    return { name, desc, relinfo };
  };

  // Modified relations rendering
  const renderRelationships = (relationships) => {
    return relationships.map((rel, index) => (
      <RelationItem key={index} relation={rel} />
    ));
  };

  const getRelationClassImmediateParents = (_relClassNode) => {
    const relClass = cy.$id(_relClassNode.id());
    const parentClasses = cy
      .collection([relClass])
      .outgoers("edge[label='REL_ISA']")
      .targets();
    return parentClasses;
  };

  const getRelationClassScopes = (_relClassNode) => {
    const relClass = cy.$id(_relClassNode.id());
    const relScopes = new Set(relClass.data().props.scope);
    const parentClasses = getRelationClassImmediateParents(relClass);

    if (parentClasses.length > 1) {
      let parentScopes = parentClasses.reduce(
        (scopes, n) => new Set([...scopes, ...getRelationClassScopes(n)]),
        new Set()
      );
      return new Set([...relScopes, ...parentScopes]);
    } else {
      return relScopes;
    }
  };

  const getRelationsForClass = (class_id, cy) => {
    const cls = cy.$id(class_id);
    const cls_isas = cls.successors('edge[label="ISA"]').connectedNodes();

    const avail_rels = {
      from: cls_isas
        .connectedEdges('edge[label="DOMAIN"]')
        .sources()
        .filter((n) => getRelationClassScopes(n).has("admin")),
      to: cls_isas
        .connectedEdges('edge[label="RANGE"]')
        .sources()
        .filter((n) => getRelationClassScopes(n).has("admin")),
    };

    return avail_rels;
  };

  const handleCreateNode = async (nodeId) => {
    try {
      await dispatch(createGraphNode({ userId, nodeId })).unwrap();
    } catch (error) {
      toast.error("Failed to create node");
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleInputChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleUpload = async () => {
    try {
      // Check if the file already exists
      const response = await api.post("/document/check-file", {
        filename: extractFilename(file.name),
      });

      if (response && response.data.res === false) {
        const formDataToSend = new FormData();
        formDataToSend.append("name", extractFilename(file.name));
        formDataToSend.append("size", file.size);
        formDataToSend.append("type", file.type);
        formDataToSend.append("file", file);

        const filePathResponse = await api.post("/document/", formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        setFilepath(filePathResponse.data.filepath);
        toast.success("File uploaded successfully.");
      } else {
        toast.error(
          "File already exists. Please try uploading a different file."
        );
      }
    } catch (error) {
      toast.error(
        error.response.data.error || "File upload failed. Please try again."
      );
      console.error("File upload error:", error);
    }
  };

  const handleDelete = async (fileName) => {
    try {
      const payload = { path: fileName };
      await api.delete(`/document/${fileName}/`, { data: payload });

      // Reset the attachment and filepath
      setFile(null);
      setFilepath("");
      toast.success("File deleted successfully.");

      // Clear the file input to allow re-selection of the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Reset file input to ensure re-upload works
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Error deleting file.");
    }
  };

  const [isAttachmentConfirmed, setIsAttachmentConfirmed] = useState(false);

  const handleCreateCheck = () => {
    // Check if the name field is empty
    if (!formData.name || formData.name.trim() === "") {
      toast.error("Name cannot be empty");
      return; // Exit early if validation fails
    }

    // Show confirmation modal if attachment is required but missing
    if (
      currentNodeData.props.fields &&
      currentNodeData.props.fields.attachment &&
      !filepath &&
      !isAttachmentConfirmed
    ) {
      setShowModal(true); // Show the modal if attachment confirmation is needed
      return; // Exit early to wait for confirmation
    }

    // Proceed to create node if all checks pass
    handleCreate();
  };

  const handleCreate = async () => {
    const payload = {
      class_name: currentNodeData.class,
      name: formData.name,
      text: formData.text,
    };

    // Add all fields from props to the payload, setting empty string if not provided in formData
    if (currentNodeData.props.fields) {
      Object.entries(currentNodeData.props.fields).forEach(([fieldName]) => {
        payload[fieldName] =
          formData[fieldName] !== undefined ? formData[fieldName] : "";
      });
    }

    if (filepath) {
      payload.attachment = filepath;
    }

    try {
      const response = await api.post("/instances/", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      handleCreateNode(response.data._id);
      toast.success("Node created successfully");
      handleClose();
    } catch (error) {
      console.error("Error creating instance:", error);
      toast.error(
        "Failed to create instance. Please check your data and try again."
      );
    }
  };

  if (
    !currentNodeData ||
    !currentNodeData.labels ||
    !currentNodeData.props ||
    !currentNodeData.props.fields
  ) {
    return null;
  }

  const getLabelText = (originalLabel) => {
    switch (originalLabel) {
      case "Literal Text":
        return "Content";
      case "Condition":
        return "Meaning";
      default:
        return originalLabel;
    }
  };

  const renderFormField = (fieldName, fieldData) => {
    switch (fieldData.type) {
      case "str":
      case "str-long":
        if (fieldData.label === "Others" && !showOthersField) {
          return null;
        }
        return (
          <Form.Group
            controlId={`form${fieldName}`}
            className="mt-3"
            key={fieldName}
          >
            <Form.Label>{getLabelText(fieldData.label)}</Form.Label>
            <Form.Control
              as={fieldData.type === "str-long" ? "textarea" : "input"}
              rows={fieldData.type === "str-long" ? 3 : undefined}
              name={fieldName}
              placeholder={`Enter ${getLabelText(fieldData.label)}`}
              onChange={handleInputChange}
            />
          </Form.Group>
        );
      case "email":
        return (
          <Form.Group
            controlId={`form${fieldName}`}
            className="mt-3"
            key={fieldName}
          >
            <Form.Label>{fieldData.label}</Form.Label>
            <Form.Control
              type="email"
              name={fieldName}
              placeholder={`Enter ${fieldData.label}`}
              onChange={handleInputChange}
            />
          </Form.Group>
        );
      case "list":
        const getListOptions = (fieldKey) => {
          // Define options for different field keys
          if (fieldKey === "C") {
            return [
              "Company",
              "Individual",
              "Limited Liability Partnership",
              "Other",
              "Partnership",
              "Sole Trader",
              "Unincorporated Association",
            ];
          }
          if (fieldKey === "F") {
            return ["Yes", "No"];
          }
          return []; // Default empty list if no matching field key
        };

        return (
          <Form.Group
            controlId={`form${fieldName}`}
            className="mt-3"
            key={fieldName}
          >
            <Form.Label>{fieldData.label}</Form.Label>
            <Form.Control
              as="select"
              name={fieldName}
              onChange={(e) => {
                handleInputChange(e);
                // Show/hide Others field based on selection
                setShowOthersField(e.target.value === "Other");
                // Clear the Others field value when switching away from Other
                if (e.target.value !== "Other") {
                  setFormData((prev) => ({
                    ...prev,
                    Others: "",
                  }));
                }
              }}
            >
              <option value="">None</option>
              {getListOptions(fieldName).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        );

      case "token":
        return (
          <Form.Group
            controlId={`form${fieldName}`}
            className="mt-3"
            key={fieldName}
          >
            <Form.Label>{fieldData.label}</Form.Label>
            <Form.Control
              type="text"
              name={fieldName}
              placeholder={`Enter ${fieldData.label}`}
              onChange={handleInputChange}
            />
          </Form.Group>
        );
      case "file":
        return (
          <Form.Group
            controlId={`form${fieldName}`}
            className="mt-3"
            key={fieldName}
          >
            <Form.Label>{fieldData.label}</Form.Label>
            <Form.Control
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <div
              className="d-flex justify-content-end"
              style={{ marginTop: "10px" }}
            >
              {filepath ? (
                <Button
                  variant="danger"
                  onClick={() => handleDelete(filepath)}
                  className="del-rem"
                >
                  Delete
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleUpload}
                  className="create-button"
                  disabled={!file}
                >
                  Upload
                </Button>
              )}
            </div>
          </Form.Group>
        );
      default:
        return null;
    }
  };

  const handleConfirm = () => {
    setIsAttachmentConfirmed(true);
    setShowModal(false); // Close the modal
    handleCreate(); // Proceed with node creation
  };

  const handleCloseModal = () => {
    setShowModal(false); // Close the modal
  };

  return (
    <>
      <Rnd
        size={size}
        onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
        onResize={(e, direction, ref, delta, position) => {
          setSize({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
          });
          setPosition(position);
        }}
        style={{
          display: show ? "flex" : "none",
          flexDirection: "column",
          background: "white",
          boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
          border: "1px solid #ddd",
          zIndex: 1000,
          position: "fixed",
        }}
        bounds="window"
        dragHandleClassName="side-panel-header" // Allow dragging only by this class
        minWidth={350}
      >
        <div
          className={`${show ? "open" : ""}`}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* <div className={`${show ? "open" : ""}`}> */}
          <div className="side-panel-header dragger m-0">
            <h5>{currentNodeData.labels[0] || "Node"}</h5>
            <button className="close-btn" onClick={handleClose}>
              &times;
            </button>
          </div>
          <div
            className="side-panel-body"
            style={{
              overflowY: "auto",
              height: parseInt(window.innerHeight) - 230,
            }}
          >
            <Description
              currentNodeData={currentNodeData}
              handleClassClick={handleClassClick}
            />

            {relationInfo.length > 0 && (
              <div className="mt-3">
                <h6>Relations:</h6>
                <ul>{renderRelationships(relationInfo)}</ul>
              </div>
            )}
            <Form
              onSubmit={(e) => {
                e.preventDefault(); // Prevent form submission
                handleCreateCheck(); // Call the custom method
              }}
            >
              {currentNodeData.props.fields &&
                (currentNodeData.class === "PlaybookRule"
                  ? orderedFieldKeysPlaybookRule.map((fieldKey) =>
                      currentNodeData.props.fields[fieldKey]
                        ? renderFormField(
                            fieldKey,
                            currentNodeData.props.fields[fieldKey]
                          )
                        : null
                    )
                  : Object.entries(currentNodeData.props.fields).map(
                      ([fieldName, fieldData]) =>
                        renderFormField(fieldName, fieldData)
                    ))}
            </Form>
          </div>
          <div className="side-panel-footer">
            {currentNodeData.labels.includes("Any") ? null : (
              <Button
                variant="primary"
                onClick={handleCreateCheck}
                className="create-button"
                disabled={currentNodeData.labels.includes("Group")}
              >
                Create {currentNodeData.labels[0] || "Node"}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handleClose}
              className="cance-button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Rnd>
      <ConfirmationModal
        show={showModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        message="You haven't attached a file. Do you want to proceed without an attachment?"
      />
    </>
  );
};

export default CreateNode;
